import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ADMIN_NAV } from "../../config/adminNav";
import { buildVisibleNav } from "../../helpers/adminNav";

// The pages admins open daily. The sidebar remains the full navigation; these
// carry their own labels because a sub-item name like "All" only reads
// correctly under its group heading.
const SHORTCUTS: { id: string; label: string }[] = [
  { id: "tx-all", label: "Transactions" },
  { id: "cu-users", label: "Users" },
  { id: "pa-all", label: "Partners" },
  { id: "an-activity", label: "Activity" },
  { id: "co-popup-campaigns", label: "Pop-up Campaigns" },
];

const QuickLinks: React.FC = () => {
  const { hasPermission } = useAuth();

  // Reuses the sidebar's own tree and permission filter, so a shortcut can
  // never point at a page the admin is not allowed to open, and the two lists
  // cannot drift apart the way the old tile grid did.
  const links = useMemo(() => {
    const leaves = buildVisibleNav(ADMIN_NAV, hasPermission)
      .flatMap((section) => section.items)
      .flatMap((item) => (item.kind === "group" ? item.items : []));
    return SHORTCUTS.flatMap((shortcut) => {
      const leaf = leaves.find((candidate) => candidate.id === shortcut.id);
      return leaf ? [{ ...shortcut, path: leaf.path }] : [];
    });
  }, [hasPermission]);

  if (links.length === 0) return null;

  return (
    <nav aria-label="Shortcuts" className="flex flex-wrap gap-2">
      {links.map((link) => (
        <Link
          key={link.id}
          to={link.path}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm text-gray-700 bg-white shadow-sm hover:bg-primary-100 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          {link.label}
          <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
        </Link>
      ))}
    </nav>
  );
};

export default QuickLinks;
